import {
	ExternalComponentBundlingError,
	ExternalComponentFetchError,
	ExternalComponentFileNotFoundError,
	ExternalComponentRendererError,
	ExternalComponentTimeoutError,
} from '@wix/external-component-renderer'

const DEFAULT_FETCH_TIMEOUT = 9000
const DEFAULT_FETCH_MAX_RETRIES = 1

type FetchFn = (url: string, init?: RequestInit) => Promise<Response>
type FetchOptions = {
	timeout?: number
	maxRetries?: number
	fetchFn?: FetchFn
} & Record<string, any>

const BUNDLE_URL_REGEXP = /(.*)\/(components.*)(.jsx|.js)/i
const getFileShortUrl = (url: string): string => {
	const isBundleUrl = BUNDLE_URL_REGEXP.test(url)
	if (isBundleUrl) {
		const [, , relativePath, fileExtension] = url.match(BUNDLE_URL_REGEXP)!
		return `${relativePath}${fileExtension}`
	}

	return url
}

export const getFetchWithTimeoutFunc = (options: FetchOptions) => {
	const {
		maxRetries = DEFAULT_FETCH_MAX_RETRIES,
		timeout = DEFAULT_FETCH_TIMEOUT,
		fetchFn = fetch,
		...fetchOptions
	} = options

	const fetchWithTimeout = async (url: string, abortOnTimeout: boolean): Promise<Response> => {
		const abortController = new AbortController()
		const { signal } = abortController

		return new Promise((resolve, reject) => {
			let timeoutOccurred = false
			setTimeout(() => {
				timeoutOccurred = true
				reject(new ExternalComponentTimeoutError())
				if (abortOnTimeout) {
					abortController.abort()
				}
			}, timeout)
			fetchFn(url, { ...fetchOptions, signal })
				.then((response) => {
					if (timeoutOccurred) {
						// stream the data to complete the request
						response.text()
					} else {
						resolve(response)
					}
				})
				.catch((error) => reject(error))
		})
	}

	const tryFetch = async (url: string, abortOnTimeout = true, retryCount = 0): Promise<Response> => {
		try {
			const response = await fetchWithTimeout(url, abortOnTimeout)

			if (response.ok) {
				return response
			} else {
				if (response.status === 404) {
					throw new ExternalComponentFileNotFoundError(getFileShortUrl(url))
				} else if (response.status === 409) {
					const { error } = await response.json()
					throw new ExternalComponentBundlingError(getFileShortUrl(url), error)
				} else {
					throw new Error(`Failed to fetch "${url}" with status ${response.status}`)
				}
			}
		} catch (e) {
			if (e instanceof ExternalComponentTimeoutError) {
				if (retryCount < maxRetries) {
					return tryFetch(url, abortOnTimeout, retryCount + 1)
				} else {
					throw new ExternalComponentTimeoutError(`Failed to fetch ${url} with timeout ${timeout}ms`)
				}
			} else {
				throw e instanceof ExternalComponentRendererError ? e : new ExternalComponentFetchError(e?.message, e)
			}
		}
	}

	return tryFetch
}
